import { Box, useTheme } from '@mui/material';
import { DropdownChip } from 'sharedComponents/Chip/DropdownChip';
import { DropdownChipOption } from 'sharedComponents/Chip/types';
import { HiringRequestStatus } from 'utils/typedEnums';
import { useMutationUpdateHiringRequest } from '../../../api/hiringRequestMutations';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { ATSConnectHiringRequestTestIds } from 'data-testids/ATS';
import auth from 'utils/auth';
import { trackHiringRequestActioned } from 'utils/pendoTracking';
import { Chip } from 'sharedComponents/Chip';
import { useState } from 'react';
import { HiringRequestStatusDropdownProps, SelectedDisplay } from '../../../types';
import { useQueryGetPostHiringPoolStatuses } from '../../../api/hiringRequestQueries';

export const HiringRequestStatusDisplay: React.FC<HiringRequestStatusDropdownProps> = ({
  status,
  actionsComplete,
  hiringRequestId,
}) => {
  const theme = useTheme();
  const districtId = auth.getUser()?.profile?.district.id;
  const { mutate, isError, reset } = useMutationUpdateHiringRequest(districtId);
  const errorMessage = 'Something went wrong. Please refresh the page and try again.';
  const { data: postHiringPoolStatuses } = useQueryGetPostHiringPoolStatuses(districtId);
  const hasApprovalStatus = postHiringPoolStatuses?.some(
    (status) => status.is_hiring_request_approved_status
  );
  const [hasError, setHasError] = useState(false);

  const placeholderOption =
    status === HiringRequestStatus.PENDING && actionsComplete
      ? {
          label: 'Select status',
          value: 'select',
          colors: {
            background: theme.palette.white.main,
            text: theme.palette.black.dark,
            border: theme.palette.black.dark,
          },
        }
      : undefined;

  const dropdownOptions: DropdownChipOption<HiringRequestStatus>[] = [
    {
      label: 'Approved',
      value: HiringRequestStatus.APPROVED,
      colors: { background: theme.palette.primary.main, text: theme.palette.white.main },
    },
    {
      label: 'Rejected',
      value: HiringRequestStatus.REJECTED,
      colors: { background: theme.palette.error.main, text: theme.palette.white.main },
    },
  ];

  let defaultSelected: DropdownChipOption<HiringRequestStatus> | undefined = undefined;
  if (status !== HiringRequestStatus.PENDING) {
    defaultSelected = dropdownOptions.find((o) => o.value === status);
  }

  const [selectedOption, setSelectedOption] = useState<
    DropdownChipOption<HiringRequestStatus> | undefined
  >(defaultSelected);

  const handleSelect = (option: DropdownChipOption<HiringRequestStatus>) => {
    if (option.value !== status) {
      if (!hasApprovalStatus && option.value === HiringRequestStatus.APPROVED) {
        return setHasError(true);
      }

      const previousOption = selectedOption;
      setSelectedOption(option);

      mutate(
        {
          hiringRequestId,
          payload: { status: option.value },
        },
        {
          onSuccess: (data) => {
            trackHiringRequestActioned({
              hiring_request_id: hiringRequestId,
              action: option.value,
              actioned_by: data.actioned_by,
            });
          },
          onError: () => {
            setSelectedOption(previousOption);
          },
        }
      );
    }
  };

  if (auth.isDistrictAdmin() && actionsComplete && status === HiringRequestStatus.PENDING) {
    return (
      <Box onClick={(event) => event.stopPropagation()}>
        <ErrorSnackbar
          message={
            hasError
              ? 'There is no selected status for approved hiring requests. Please select one in settings.'
              : errorMessage
          }
          onClose={hasError ? () => setHasError(false) : () => reset()}
          open={hasError || isError}
          dataTestId={ATSConnectHiringRequestTestIds.STATUS_ERROR_SNACKBAR}
        />
        <DropdownChip
          options={dropdownOptions}
          handleSelect={handleSelect}
          placeholderOption={placeholderOption}
          dataTestId={ATSConnectHiringRequestTestIds.STATUS_DROPDOWN}
          selected={selectedOption}
        />
      </Box>
    );
  }

  let selectedDisplay: SelectedDisplay = selectedOption;
  if (!selectedDisplay) {
    selectedDisplay = {
      label: 'Pending',
      colors: {
        background: theme.palette.gray.lightest,
        text: theme.palette.black.dark,
        border: theme.palette.gray.darker,
      },
    };
  }
  return (
    <Chip
      label={selectedDisplay.label}
      sx={{
        width: '133px',
        height: theme.spacing(4),
        backgroundColor: selectedDisplay.colors.background,
        color: selectedDisplay.colors.text,
        border: selectedDisplay.colors.border
          ? `2px solid ${selectedDisplay.colors.border}`
          : 'none',
        cursor: 'pointer',
        fontWeight: theme.typography.fontWeightMedium,
        fontSize: theme.typography.body2.fontSize,
      }}
      dataTestId={ATSConnectHiringRequestTestIds.STATUS_CHIP}
    />
  );
};
